import { useState, useEffect } from 'react';
import { useUser } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { customersService } from '../services/api';
import { getCustomerDisplayName, getCustomerIdDisplay } from '../utils/customerHelpers';
import CustomerCreateModal from './shared/CustomerCreateModal';
import PermissionGate from './shared/PermissionGate';
import { PERMISSIONS } from '../constants/permissionMappings';

function Customers() {
  const { user } = useUser();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  // Check for openModal query parameter on mount and when location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openModal = searchParams.get('openModal');
    
    if (openModal === 'customer') {
      setShowCreateForm(true);
      // Remove the query parameter from the URL without refreshing the page
      window.history.replaceState({}, '', '/merchant/customers');
    }
  }, [location]);

  // Fetch customers when component mounts
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const response = await customersService.getMerchantCustomers();

        // Log all customer data to debug
        console.log("Fetched customers:", response);

        setCustomers(response);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        setError('Failed to fetch customers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [user?.id]);

  // Handle customer creation
  const handleCustomerCreated = (newCustomer) => {
    // Add the new customer to the list
    setCustomers([...customers, newCustomer]);
    
    // Show success message
    setCreateSuccess(true);
    setTimeout(() => setCreateSuccess(false), 3000);
  };

  // Use centralized customer display logic
  const getCustomerDisplayNameForTable = (customer) => {
    const displayName = getCustomerDisplayName(customer);
    if (displayName !== 'Guest') return displayName;
    
    // Special fallback for customers table
    const idDisplay = getCustomerIdDisplay(customer);
    return customer.email || idDisplay || 'Unknown';
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-[2000px] w-full mx-auto px-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Customers</h1>
          <PermissionGate permission={PERMISSIONS.customers.create}>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-brand text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors focus:outline-none"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Create
            </button>
          </PermissionGate>
        </div>

        {createSuccess && (
          <div className="mb-4 p-3 bg-green-100 text-green-800 rounded">
            Customer created successfully!
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">
            {error}
          </div>
        )}

        {/* Customer Create Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative max-h-[90vh] overflow-y-auto">
              {/* Close button */}
              <button 
                onClick={() => setShowCreateForm(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4 pr-6">New Customer</h2>
                <CustomerCreateModal
                  isOpen={showCreateForm}
                  onClose={() => setShowCreateForm(false)}
                  onCustomerCreated={handleCustomerCreated}
                />
              </div>
            </div>
          </div>
        )}

        {/* Customers Table */}
        {loading && !showCreateForm ? (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          </div>
        ) : customers.length > 0 ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {getCustomerDisplayNameForTable(customer)}
                        </div>
                        {customer.company && (customer.firstName || customer.lastName) && (
                          <div className="text-sm text-gray-500">
                            {`${customer.firstName || ''} ${customer.lastName || ''}`.trim()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{customer.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{customer.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {[customer.city, customer.state].filter(Boolean).join(', ') || '-'}
                        </div>
                        {customer.country && <div className="text-sm text-gray-500">{customer.country}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(customer.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/merchant/customers/${customer.externalId}`}
                          className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand transition-colors"
                        >
                          <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-10 bg-white rounded-lg shadow">
            <svg 
              className="mx-auto h-12 w-12 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" 
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No customers yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new customer.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand hover:bg-brand-hover focus:outline-none"
              >
                <svg 
                  className="-ml-1 mr-2 h-5 w-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create Customer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Customers;